import { forwardRef, useEffect, useState } from 'react';

// material-ui
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Slide,
    TextField
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// project imports
import { gridSpacing, TRANSACTION_STATUS, TRANSACTION_TRANSFER_STATUS, BOOKING_TYPES, USER_TYPE, DELETED_STATUS } from 'store/constant';
import useApi from 'hooks/useApi';
import VenueService from 'services/VenueService';
import UserService from 'services/UserService';
import transactionFilterSchema from 'utils/schema/TransactionFilterSchema';

// third party
import moment from 'moment';
import { useFormik } from 'formik';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

// animation
const Transition = forwardRef((props, ref) => <Slide direction="left" ref={ref} {...props} />);

const Filter = ({ filterObj, open, handleCloseDialog, setFilterObj, viewType, setPage }) => {

    const [venueArray, setVenueArray] = useState([]);
    const [userArray, setUserArray] = useState([]);

    const filterTransaction = () => {
        const formikValues = { ...formik.values };
        if (formikValues.start_date) {
            formikValues.start_date = moment(formikValues.start_date, 'ddd MMM DD YYYY').format('YYYY-MM-DD')
        }
        if (formikValues.end_date) {
            formikValues.end_date = moment(formikValues.end_date, 'ddd MMM DD YYYY').format('YYYY-MM-DD')
        }
        setFilterObj(formikValues);
        setPage(1);
        handleCloseDialog();
    };

    const resetFilterForm = () => {
        formik.resetForm();
        setFilterObj({
            start_date: null,
            end_date: null,
            uuid_venue: viewType ? [filterObj.uuid_venue] : [],
            uuid_user: [],
            status: [],
            transfer_status: [],
            booking_type: [],
            deleted: []
        });
        setPage(1);
    };

    const venueListHandler = (response) => {
        setVenueArray(response.data);
    };

    const userListHandler = (response) => {
        setUserArray(response.data);
    };

    const getVenueListApi = useApi(VenueService.getVenueDropdownList, venueListHandler);
    const getUserListApi = useApi(UserService.getDropdownUser, userListHandler);

    const formik = useFormik({
        initialValues: {
            start_date: null,
            end_date: null,
            uuid_venue: [],
            uuid_user: [],
            status: [],
            transfer_status: [],
            booking_type: [],
            deleted: []
        },
        validationSchema: transactionFilterSchema,
        onSubmit: filterTransaction
    });

    const setFormValue = () => {
        formik.setFieldValue('start_date', filterObj.start_date ? moment(filterObj.start_date).format('ddd MMM DD YYYY') : null);
        formik.setFieldValue('end_date', filterObj.end_date ? moment(filterObj.end_date).format('ddd MMM DD YYYY') : null);
        formik.setFieldValue('uuid_venue', filterObj.uuid_venue.length ? filterObj.uuid_venue : []);
        formik.setFieldValue('uuid_user', filterObj.uuid_user.length ? filterObj.uuid_user : []);
        formik.setFieldValue('status', filterObj.status.length ? filterObj.status : []);
        formik.setFieldValue('transfer_status', filterObj.transfer_status.length ? filterObj.transfer_status : []);
        formik.setFieldValue('booking_type', filterObj.booking_type.length ? filterObj.booking_type : []);
        formik.setFieldValue('deleted', filterObj.deleted ? filterObj.deleted : []);
    };

    useEffect(() => {
        getVenueListApi.execute();
        getUserListApi.execute({ type: [USER_TYPE[1].value] });
        if (filterObj) {
            setFormValue();
        }
    }, []);

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleCloseDialog}
            sx={{
                '&>div:nth-of-type(3)': {
                    justifyContent: 'flex-end',
                    '&>div': {
                        m: 0,
                        borderRadius: '0px',
                        maxWidth: 450,
                        maxHeight: '100%',
                        height: '100%'
                    }
                }
            }}
        >
            {open && (
                <>
                    <DialogTitle>Filter</DialogTitle>
                    <DialogContent>
                        <Grid container spacing={gridSpacing} sx={{ mt: 0.25 }}>
                            <Grid item xs={12}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="Start Date"
                                        views={["year", "month", "day"]}
                                        inputFormat="dd-MM-yyyy"
                                        onChange={(date) => formik.setFieldValue("start_date", date ? date : "")}
                                        onBlur={formik.handleBlur}
                                        disableFuture
                                        value={formik.values.start_date}
                                        renderInput={(params) => <TextField fullWidth name="start_date" onBlur={formik.handleBlur} autoComplete="off"
                                            value={formik.values.start_date} onChange={formik.handleChange} onKeyDown={(e) => { e.preventDefault(); }} size="medium" {...params} />}
                                    />
                                </LocalizationProvider>
                                {formik.touched.start_date && formik.errors.start_date && (
                                    <FormHelperText error id="standard-weight-helper-date-start_date-filter">
                                        {formik.errors.start_date}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="End Date"
                                        views={["year", "month", "day"]}
                                        inputFormat="dd-MM-yyyy"
                                        onChange={(date) => formik.setFieldValue("end_date", date ? date : "")}
                                        onBlur={formik.handleBlur}
                                        disableFuture
                                        value={formik.values.end_date}
                                        renderInput={(params) => <TextField fullWidth name="end_date" onBlur={formik.handleBlur} autoComplete="off"
                                            value={formik.values.end_date} onChange={formik.handleChange} onKeyDown={(e) => { e.preventDefault(); }} size="medium" {...params} />}
                                    />
                                </LocalizationProvider>
                                {formik.touched.end_date && formik.errors.end_date && (
                                    <FormHelperText error id="standard-weight-helper-date-end_date-filter">
                                        {formik.errors.end_date}
                                    </FormHelperText>
                                )}
                            </Grid>
                            {
                                viewType ? (<></>) : (
                                    <Grid item xs={12}>
                                        <FormControl captionLabel='Venue' fullWidth size="medium">
                                            <InputLabel id="venue-select-label">Venue</InputLabel>
                                            <Select
                                                labelId="venue-select-label"
                                                id="venue-select"
                                                label="venue"
                                                name="uuid_venue"
                                                size="medium"
                                                multiple
                                                error={Boolean(formik.touched.uuid_venue && formik.errors.uuid_venue)}
                                                value={formik.values.uuid_venue}
                                                onBlur={formik.handleBlur}
                                                onChange={formik.handleChange}
                                            >
                                                {
                                                    venueArray.map((venueObj, index) => {
                                                        return (
                                                            <MenuItem key={index} value={venueObj.uuid}>{venueObj.venue_name}</MenuItem>
                                                        )
                                                    })
                                                }
                                            </Select>
                                        </FormControl>
                                        {formik.touched.uuid_venue && formik.errors.uuid_venue && (
                                            <FormHelperText error id="standard-weight-helper-text-uuid_venue-filter">
                                                {formik.errors.uuid_venue}
                                            </FormHelperText>
                                        )}
                                    </Grid>
                                )
                            }
                            <Grid item xs={12}>
                                <FormControl captionLabel='User' fullWidth size="medium">
                                    <InputLabel id="user-select-label">User</InputLabel>
                                    <Select
                                        labelId="user-select-label"
                                        id="user-select"
                                        label="user"
                                        name="uuid_user"
                                        multiple
                                        error={Boolean(formik.touched.uuid_user && formik.errors.uuid_user)}
                                        value={formik.values.uuid_user}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        MenuProps={MenuProps}
                                    >
                                        {
                                            userArray.map((userObj, index) => {
                                                return (
                                                    <MenuItem key={index} value={userObj.uuid}>{userObj.firstname} {userObj.lastname}</MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                </FormControl>
                                {formik.touched.uuid_user && formik.errors.uuid_user && (
                                    <FormHelperText error id="standard-weight-helper-text-uuid_user-filter">
                                        {formik.errors.uuid_user}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl captionLabel='Status' fullWidth size="medium">
                                    <InputLabel id="status-select-label">Payment Status</InputLabel>
                                    <Select
                                        labelId="status-select-label"
                                        id="status-select"
                                        label="status"
                                        name="status"
                                        multiple
                                        error={Boolean(formik.touched.status && formik.errors.status)}
                                        value={formik.values.status}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                    >
                                        {
                                            TRANSACTION_STATUS.map((trnStatus, index) => {
                                                return (
                                                    <MenuItem key={index} value={trnStatus.value}>{trnStatus.name}</MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                </FormControl>
                                {formik.touched.status && formik.errors.status && (
                                    <FormHelperText error id="standard-weight-helper-text-status-filter">
                                        {formik.errors.status}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl captionLabel='Transfer Status' fullWidth size="medium">
                                    <InputLabel id="transfer-select-label">Transfer Status</InputLabel>
                                    <Select
                                        labelId="transfer-select-label"
                                        id="transfer-select"
                                        label="transfer"
                                        name="transfer_status"
                                        multiple
                                        error={Boolean(formik.touched.transfer_status && formik.errors.transfer_status)}
                                        value={formik.values.transfer_status}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                    >
                                        {
                                            TRANSACTION_TRANSFER_STATUS.map((trnStatus, index) => {
                                                return (
                                                    <MenuItem key={index} value={trnStatus.value}>{trnStatus.name}</MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                </FormControl>
                                {formik.touched.transfer_status && formik.errors.transfer_status && (
                                    <FormHelperText error id="standard-weight-helper-text-transfer_status-filter">
                                        {formik.errors.transfer_status}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl captionLabel='Booking Type' fullWidth size="medium">
                                    <InputLabel id="booking_type-select-label">Booking Type</InputLabel>
                                    <Select
                                        labelId="booking_type-select-label"
                                        id="booking_type-select"
                                        label="booking_type"
                                        name="booking_type"
                                        multiple
                                        error={Boolean(formik.touched.booking_type && formik.errors.booking_type)}
                                        value={formik.values.booking_type}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                    >
                                        {
                                            BOOKING_TYPES.map((trnStatus, index) => {
                                                return (
                                                    <MenuItem key={index} value={trnStatus.value}>{trnStatus.name}</MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                </FormControl>
                                {formik.touched.booking_type && formik.errors.booking_type && (
                                    <FormHelperText error id="standard-weight-helper-text-booking_type-filter">
                                        {formik.errors.booking_type}
                                    </FormHelperText>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl captionLabel='Status' fullWidth size="medium">
                                    <InputLabel id="deleted-select-label">Status</InputLabel>
                                    <Select
                                        labelId="deleted-select-label"
                                        id="deleted-select"
                                        label="deleted"
                                        name="deleted"
                                        error={Boolean(formik.touched.deleted && formik.errors.deleted)}
                                        value={formik.values.deleted}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        multiple
                                    >
                                        {
                                            DELETED_STATUS.map((deleted_status, index) => {
                                                return (
                                                    <MenuItem key={index} value={deleted_status.value}>{deleted_status.name}</MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                </FormControl>
                                {formik.touched.deleted && formik.errors.deleted && (
                                    <FormHelperText error id="standard-weight-helper-text-status-type-filter">
                                        {formik.errors.deleted}
                                    </FormHelperText>
                                )}
                            </Grid>
                        </Grid>
                        <Grid container spacing={gridSpacing} sx={{ mt: 3, justifyContent: 'right' }}>
                            <Button variant="contained" color="inherit" onClick={handleCloseDialog} sx={{ marginRight: '10px' }}>
                                Close
                            </Button>
                            <Button variant="contained" color="error" onClick={resetFilterForm} sx={{ marginRight: '10px' }}>
                                Reset
                            </Button>
                            <Button variant="contained" color="primary" onClick={formik.handleSubmit}>
                                Filter
                            </Button>
                        </Grid>
                    </DialogContent>
                </>
            )}
        </Dialog>
    );
};

export default Filter;
